import { getPlatformInfo } from '@/src/renderer/lib/platform'
import { APP_VERSION } from '@/src/renderer/lib/data/openSourceLicenses'

/**
 * **Onboarding-Antworten anonym übertragen** (25.09.2026) — auch ohne Konto.
 * Aufgaben und Notizen bleiben lokal; die Antworten aus „Wofür" und „Wobei"
 * gehen als eine Zeile nach Supabase (`onboarding_responses`), ausgewertet wird
 * im Dashboard. Plan: `.claude/tasks/onboarding-answers-local-upload.md`.
 *
 * Die Zeile trägt **keine Kennung** — keine Nutzer-, keine Geräte-ID. Das ist
 * die Absprache mit „lokal": nichts, was sich einem Menschen zuordnen lässt.
 */

export interface OnboardingAnswers {
  purposes: string[]
  intents: string[]
  /** Sprache der App beim Ausfüllen, z. B. `de` oder `en-US`. */
  locale: string
}

/** Genau die Spalten von `onboarding_responses`, die der Client setzen darf. */
export interface OnboardingResponseRow {
  purposes: string[]
  intents: string[]
  platform: 'electron-mac' | 'electron-win' | 'electron-linux' | 'web'
  app_version: string
  locale: string
}

/** Sendet eine Zeile; `true` bei Erfolg. Kommt aus der Kontoquelle (`useAccountSource`). */
export type SubmitOnboardingResponse = (row: OnboardingResponseRow) => Promise<boolean>

/** Merker für eine Zeile, die beim Setup nicht rausging (kein Netz). */
export const PENDING_KEY = 'pendingOnboardingResponse'

export function buildOnboardingRow({ purposes, intents, locale }: OnboardingAnswers): OnboardingResponseRow {
  const info = getPlatformInfo()
  const platform: OnboardingResponseRow['platform'] = !info.isElectron
    ? 'web'
    : info.isWindows ? 'electron-win' : info.isLinux ? 'electron-linux' : 'electron-mac'
  return { purposes, intents, platform, app_version: APP_VERSION, locale }
}

async function trySubmit(submit: SubmitOnboardingResponse, row: OnboardingResponseRow): Promise<boolean> {
  try {
    return await submit(row)
  } catch {
    return false
  }
}

/**
 * Sendet die Antworten. **Erst merken, dann senden:** Nach dem Setup wechselt
 * die App ins Onboarding-Fenster, eine laufende Anfrage kann dabei abreißen.
 * Der Merker liegt deshalb schon vorher und fällt erst bei Erfolg weg; was
 * nicht rausging, holt {@link flushPendingOnboardingResponse} beim nächsten
 * Start nach. Wirft nie — das Setup wartet auf nichts.
 */
export async function sendOnboardingResponse(submit: SubmitOnboardingResponse, answers: OnboardingAnswers): Promise<void> {
  const row = buildOnboardingRow(answers)
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(row))
  } catch {
    // Kein Speicher (privates Fenster): dann nur dieser eine Versuch.
  }
  if (await trySubmit(submit, row)) {
    try { localStorage.removeItem(PENDING_KEY) } catch { /* s. o. */ }
  }
}

/** Beim App-Start: eine liegengebliebene Zeile einmal nachsenden, bei Erfolg den Merker löschen. */
export async function flushPendingOnboardingResponse(submit: SubmitOnboardingResponse): Promise<void> {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(PENDING_KEY)
  } catch {
    return
  }
  if (!raw) return
  let row: OnboardingResponseRow
  try {
    row = JSON.parse(raw) as OnboardingResponseRow
  } catch {
    localStorage.removeItem(PENDING_KEY)
    return
  }
  if (await trySubmit(submit, row)) localStorage.removeItem(PENDING_KEY)
}
